import type React from "react";
import { twJoin } from "tailwind-merge";

import { ReverseLeftIcon } from "@/components/Icons/ReverseLeftIcon";
import { ReverseRightIcon } from "@/components/Icons/ReverseRightIcon";
import { Button } from "@/design-system/Button";
import { IconButton } from "@/design-system/IconButton";
import { Inline } from "@/design-system/Inline";
import { Tooltip } from "@/design-system/Tooltip";
import { useTranslation } from "@/translations";

interface AdminCreatePostFormAIHelperProps {
  onGenerate: () => void;
  onUndo: () => void;
  onRedo: () => void;
  isUndoable: boolean;
  isRedoable: boolean;
  isGenerating: boolean;
  isCopilotAvailable: boolean;
  isDisabled?: boolean;
  presentation?: "banner" | "editor";
}

/**
 * Renders Copilot generation and history controls. The `banner` presentation includes explanatory copy,
 * while `editor` renders compact controls for placement alongside the post editor.
 */
export function AdminCreatePostFormAIHelper({
  isUndoable,
  isRedoable,
  isCopilotAvailable,
  isGenerating,
  onGenerate,
  onUndo,
  onRedo,
  isDisabled,
  presentation = "banner",
}: AdminCreatePostFormAIHelperProps): React.ReactNode {
  const { t } = useTranslation();

  if (presentation === "editor") {
    return (
      <Inline gap="sm" align="center" wrap={false}>
        <div className="min-w-0 flex-1">
          <Button
            variant="ai"
            fullWidth
            onClick={onGenerate}
            isLoading={isGenerating}
            disabled={!isCopilotAvailable || isDisabled}
            title={isDisabled ? t("page.admin-create-post.form.notice.ai-copilot.tooltip.disabled") : undefined}
            data-testid="copilot-generate-btn"
          >
            {t("page.admin-create-post.redesign.ai.improve")}
          </Button>
        </div>
        {(isUndoable || isRedoable) && !isDisabled && (
          <Inline gap="sm" wrap={false}>
            <IconButton
              variant="soft"
              disabled={!isUndoable || isGenerating}
              title={t("common.action.undo")}
              icon={<ReverseLeftIcon />}
              data-testid="copilot-undo-btn"
              onClick={onUndo}
            />
            <IconButton
              variant="soft"
              disabled={!isRedoable || isGenerating}
              title={t("common.action.redo")}
              icon={<ReverseRightIcon />}
              onClick={onRedo}
            />
          </Inline>
        )}
      </Inline>
    );
  }

  return (
    <Tooltip tooltip={isDisabled ? t("page.admin-create-post.form.notice.ai-copilot.tooltip.disabled") : undefined}>
      <aside
        className={twJoin(
          "flex w-full flex-col flex-wrap items-start justify-between gap-space-md rounded-lg bg-background-brand-tertiary-legacy-light p-space-md transition-colors @md:flex-row @md:items-center",
          isDisabled && "bg-background-secondary",
        )}
      >
        <p className={twJoin("text-left text-body-emphasis", isDisabled && "text-text-disabled")}>
          {t("page.admin-create-post.form.notice.ai-copilot")}
        </p>
        <Inline gap="sm" align="center">
          <Button
            variant="ai"
            onClick={onGenerate}
            isLoading={isGenerating}
            disabled={!isCopilotAvailable || isDisabled}
            data-testid="copilot-generate-btn"
          >
            {isUndoable && !isDisabled
              ? t("page.admin-create-post.form.ai-regenerate")
              : t("page.admin-create-post.form.ai-generate")}
          </Button>
          {(isUndoable || isRedoable) && !isDisabled && (
            <>
              <IconButton
                variant="soft"
                disabled={!isUndoable}
                title={t("common.action.undo")}
                icon={<ReverseLeftIcon />}
                data-testid="copilot-undo-btn"
                onClick={onUndo}
              />
              <IconButton
                variant="soft"
                disabled={!isRedoable}
                title={t("common.action.redo")}
                icon={<ReverseRightIcon />}
                onClick={onRedo}
              />
            </>
          )}
        </Inline>
      </aside>
    </Tooltip>
  );
}
